<template>
    <component :is="'div'" class="d-flex py-1" :class="getClass(index)" v-bind="bindData" @click="onClick">
        <div v-if="checkbox().length > 0" class="ms-2">
            <Vnodes :vnodes="checkbox()" />
        </div>
        <div v-if="images().length > 0" class="ms-2">
            <Vnodes :vnodes="images()" />
        </div>
        <div class="d-flex flex-column flex-grow-1 mx-1">
            <div class="d-flex">
                <div class="flex-grow-1 mx-1 mt-1">
                    <Vnodes :vnodes="header()" />
                </div>
                <div class="ms-2">
                    <Vnodes :vnodes="buttons()" />
                </div>
            </div>
            <div v-if="label().length > 0" class="mb-1">
                <Vnodes :vnodes="label()" />
            </div>
            <div v-if="items().length > 0" class="pb-1">
                <div :class="itemsClassNames">
                    <Vnodes :vnodes="items()" />
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
import type { Header } from '../helpers';
import { travel } from '../helpers';

defineOptions({
    components: {
        Vnodes: (props: Record<string, any>, context: any) => props.vnodes
    },
    inheritAttrs: false
});

export interface Props
{
    index: number;
    item: any;
    headers: Header[];
}

const $slots = useSlots() as any;
const $attrs = useAttrs() as any;
const props = withDefaults(defineProps<Props>(), {});
const itemsClassNames = computed(() => `row row-cols-1 row-cols-sm-2 row-cols-md-3`);
const item = computed(() => props.item);
const rowClass = inject<(item: any, i: number) => Record<string, boolean> | string[] | string>('row-class');
const rowClick = inject<(item: any, index: number, e: PointerEvent) => any>('row-click');

const bindData = computed(() => ({
    'data-draggable': $attrs['data-draggable'],
    'draggable': $attrs.draggable,
    'style': $attrs.style
}));

provide('item', item);

const draggable = inject<boolean>('draggable');

const getClass = (index: number): any =>
{
    let result = { 'bg-light-subtle': index % 2 == 0, 'draggable': draggable };

    if (rowClass)
    {
        result = Object.assign(result, rowClass(props.item, props.index));
    }

    return result;
};

const onClick = (e: any): void =>
{
    if (rowClick && props.item)
        rowClick(props.item, props.index, e);
};

const cells = (): any[] =>
{
    const allowed = [
        'ListViewHeader',
        'ListViewItem',
        'ListViewLabel',
        'ListViewButtons',
        'ListViewImage',
        'ListViewCheckbox',
        'ListViewCierarchy',
        'ListViewHandle'
    ];
    const prefix = (new Date()).getTime();

    return travel($slots.default(), allowed)
        .map((p, i) =>
        {
            p.key = `${prefix}-${i}`;
            p.props = p.props || {};
            p.props.index = i;
            p.props.header = props.headers[i];

            return p;
        })
        .filter(p => !p.props.header.disabled)
        .orderBy(p => p.props.header.position)
        .toArray();
};

const filter = (tagName: string): any[] =>
{
    return cells().filter(p => p.type.name == tagName);
};

const header = (): any[] =>
{
    return filter('ListViewHeader');
};

const items = (): any[] =>
{
    return filter('ListViewItem');
};

const label = (): any[] =>
{
    return filter('ListViewLabel');
};

const buttons = (): any[] =>
{
    return filter('ListViewButtons');
};

const images = (): any[] =>
{
    return filter('ListViewImage');
};

const checkbox = (): any[] =>
{
    return filter('ListViewCheckbox');
};
</script>
